import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  cashAdvanceApi,
  type CashAdvanceStatus,
  type RequestCashAdvancePayload,
  type ApproveCashAdvancePayload,
  type ReleaseCashAdvancePayload,
  type RejectCashAdvancePayload,
} from "../lib/cashAdvanceApi";

export const CASH_ADVANCE_KEYS = {
  all: ["cashAdvance"] as const,
  mine: () => [...CASH_ADVANCE_KEYS.all, "mine"] as const,
  admin: (businessId: string, status: CashAdvanceStatus) =>
    [...CASH_ADVANCE_KEYS.all, "admin", businessId, status] as const,
};

export function useMyCashAdvances(status?: CashAdvanceStatus, businessId?: string) {
  return useInfiniteQuery({
    queryKey: [...CASH_ADVANCE_KEYS.mine(), status ?? "all", businessId ?? ""],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      cashAdvanceApi.listMyCashAdvances(status, { limit: 10, cursor: pageParam, businessId }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useRequestCashAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RequestCashAdvancePayload) =>
      cashAdvanceApi.requestCashAdvance(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CASH_ADVANCE_KEYS.mine() });
    },
  });
}

export function useAdminCashAdvances(
  businessId: string | null | undefined,
  status: CashAdvanceStatus = "PENDING",
) {
  return useQuery({
    queryKey: CASH_ADVANCE_KEYS.admin(businessId ?? "", status),
    queryFn: () => cashAdvanceApi.adminListCashAdvances(businessId!, status),
    enabled: !!businessId,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useApproveCashAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cashAdvId,
      payload,
    }: {
      cashAdvId: string;
      payload: ApproveCashAdvancePayload;
    }) => cashAdvanceApi.approveCashAdvance(cashAdvId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CASH_ADVANCE_KEYS.all });
    },
  });
}

export function useReleaseCashAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cashAdvId,
      payload,
    }: {
      cashAdvId: string;
      payload: ReleaseCashAdvancePayload;
    }) => cashAdvanceApi.releaseCashAdvance(cashAdvId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CASH_ADVANCE_KEYS.all });
    },
  });
}

export function useRejectCashAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cashAdvId,
      payload,
    }: {
      cashAdvId: string;
      payload: RejectCashAdvancePayload;
    }) => cashAdvanceApi.rejectCashAdvance(cashAdvId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CASH_ADVANCE_KEYS.all });
    },
  });
}
